import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  FormHelperText,
  Grid,
  Stack,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
} from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import RichTextEditor from "../components/RichTextEditor";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import {
  getMeetingsData,
  getMeeting,
  addOrUpdateMeeting,
  deleteMeeting,
} from "../actions/meet.actions";

// Import common components
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import SearchControls from "../components/common/SearchControls";
import DataTable from "../components/common/DataTable";
import TablePagination from "../components/common/TablePagination";
import DeleteModal from "../ui-components/DeleteModal";
import ToolBody from "../components/common/ToolBody";

// Default meeting date settings
const MEETING_DEFAULTS = {
  START_DATE: {
    DAYS_FROM_TODAY: 30,
    HOUR: 9, // 9:00 AM
    MINUTE: 0,
  },
  END_DATE: {
    DAYS_AFTER_START: 2,
    HOUR: 17, // 5:00 PM
    MINUTE: 0,
  },
};

/**
 * Generate default start date (30 days from today at 9:00 AM)
 * @returns {string} Formatted date string
 */
const getDefaultStartDate = () => {
  const now = new Date();
  now.setDate(now.getDate() + MEETING_DEFAULTS.START_DATE.DAYS_FROM_TODAY);
  now.setHours(
    MEETING_DEFAULTS.START_DATE.HOUR,
    MEETING_DEFAULTS.START_DATE.MINUTE,
    0,
    0,
  );

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:00`;
};

/**
 * Generate default end date (2 days after start date at 5:00 PM)
 * @param {string} startDateStr - Start date string to base the end date on
 * @returns {string} Formatted date string
 */
const getDefaultEndDate = (startDateStr = null) => {
  let baseDate;

  if (startDateStr) {
    baseDate = new Date(startDateStr);
  } else {
    baseDate = new Date();
    baseDate.setDate(
      baseDate.getDate() + MEETING_DEFAULTS.START_DATE.DAYS_FROM_TODAY,
    );
  }

  baseDate.setDate(
    baseDate.getDate() + MEETING_DEFAULTS.END_DATE.DAYS_AFTER_START,
  );
  baseDate.setHours(
    MEETING_DEFAULTS.END_DATE.HOUR,
    MEETING_DEFAULTS.END_DATE.MINUTE,
    0,
    0,
  );

  const year = baseDate.getFullYear();
  const month = String(baseDate.getMonth() + 1).padStart(2, "0");
  const day = String(baseDate.getDate()).padStart(2, "0");
  const hours = String(baseDate.getHours()).padStart(2, "0");
  const minutes = String(baseDate.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:00`;
};

/**
 * Formats a date string into a more readable format.
 * @param {*} dateTime - The date string to format.
 * @param {*} format - The format to use (not implemented).
 * @returns The formatted date string.
 */
const formatDate = (dateTime, format = null) => {
  if (!dateTime) {
    return "";
  }

  // Check if the datetime contains time component
  const hasTime = dateTime.includes(" ");

  let date, time;
  if (hasTime) {
    [date, time] = dateTime.split(" ");
  } else {
    date = dateTime;
  }

  const myArray = date.split("-");
  if (myArray.length !== 3) return dateTime;

  const year = myArray[0];
  const month = myArray[1];
  const day = myArray[2];

  if (hasTime) {
    const timeComponents = time.split(":");
    const hour = timeComponents[0];
    const minute = timeComponents[1];

    // Convert to AM/PM format
    const hourNum = parseInt(hour, 10);
    const ampm = hourNum >= 12 ? "PM" : "AM";
    const hour12 = hourNum % 12 || 12; // Convert to 12-hour format

    return `${month}-${day}-${year} ${hour12}:${minute} ${ampm}`;
  }

  return `${month}-${day}-${year}`;
};

/**
 * Admin: Meetings Management
 * @returns {JSX.Element}
 * @constructor
 */
const Meetings = () => {
  const editorContentRef = useRef("");
  const { storage } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  // Meeting form state
  const meetingDefault = {
    meeting_id: 0,
    meeting_title: "",
    meeting_title_maxlength: 50,
    meeting_title_error: false,
    meeting_content: "",
    meeting_content_maxlength: 5000,
    meeting_content_error: false,
    meeting_video: "",
    meeting_video_maxlength: 65535,
    meeting_video_error: false,
    meeting_location: "",
    meeting_location_maxlength: 200,
    meeting_location_error: false,
    meeting_start_dt: getDefaultStartDate(),
    meeting_start_dt_error: false,
    meeting_end_dt: getDefaultEndDate(),
    meeting_end_dt_error: false,
    meeting_image: "",
    meeting_image_name: "",
    meeting_image_error: false,
    meeting_documents: "",
    meeting_documents_name: "",
    meeting_documents_error: false,
  };

  const [formData, setFormData] = useState(meetingDefault);

  // Table data state
  const [tableData, setTableData] = useState({
    data: [],
    loading: true,
    pagination: {
      pageNo: 1,
      perPage: 50,
      totalCount: 0,
      totalPage: 1,
    },
    search: {
      text: "",
      type: "meeting_title",
      startDate: null,
      endDate: null,
    },
    sort: {
      field: "meeting_start_dt",
      direction: "desc",
    },
  });

  // Image preview state
  const [imagePreview, setImagePreview] = useState("");
  const [docPreview, setDocPreview] = useState("");

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    if (newValue === "1") {
      // Reset form when switching to Add/Edit tab
      resetForm();
    } else if (newValue === "2") {
      // Load data when switching to Manage tab
      fetchTableData();
    }
  };

  // Load meetings data
  useEffect(() => {
    if (activeTab === "2") {
      fetchTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchTableData = (params = {}) => {
    const {
      pageNo = tableData.pagination.pageNo,
      perPage = tableData.pagination.perPage,
      searchText = tableData.search.text,
      searchType = tableData.search.type,
      sortField = tableData.sort.field,
      sortDirection = tableData.sort.direction,
    } = params;

    setTableData((prev) => ({
      ...prev,
      loading: true,
    }));

    getMeetingsData({
      pageNo,
      perPage,
      searchText,
      searchType,
      orderField: sortField,
      orderDirection: sortDirection,
    })
      .then((res) => {
        if (res.data.status === true) {
          setTableData((prev) => ({
            ...prev,
            data: res.data.data || [],
            pagination: {
              ...prev.pagination,
              pageNo: pageNo,
              perPage: perPage,
              totalCount: res.data.pagination?.totalCount || 0,
              totalPage: res.data.pagination?.totalPage || 1,
            },
          }));
        } else {
          setTableData((prev) => ({
            ...prev,
            data: [],
          }));
        }
      })
      .catch((error) => {
        log.error("Failed to load meetings:", error);
        setTableData((prev) => ({
          ...prev,
          data: [],
        }));
      })
      .finally(() => {
        setTableData((prev) => ({
          ...prev,
          loading: false,
        }));
      });
  };

  // Form field change handlers
  const handleFieldChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    const { name, value } = event.target;
    const updatedFormData = { ...formData };

    const maxLengthKey = `${name}_maxlength`;
    const errorKey = `${name}_error`;

    if (
      updatedFormData[maxLengthKey] &&
      value.length <= updatedFormData[maxLengthKey]
    ) {
      updatedFormData[name] = value;
      updatedFormData[errorKey] = false;
    } else if (!updatedFormData[maxLengthKey]) {
      // If no max length is defined
      updatedFormData[name] = value;
      updatedFormData[errorKey] = false;
    }

    setFormData(updatedFormData);
  };

  // Date picker handlers
  const handleStartDateChange = (newValue) => {
    const updatedFormData = { ...formData };
    setErrorMsg("");

    if (newValue !== null) {
      let selectedDate = new Date(newValue.$d);
      const year = selectedDate.getFullYear();
      let month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      let day = String(selectedDate.getDate()).padStart(2, "0");
      let hours = String(selectedDate.getHours()).padStart(2, "0");
      let minutes = String(selectedDate.getMinutes()).padStart(2, "0");

      const newStartDate = `${year}-${month}-${day} ${hours}:${minutes}:00`;
      updatedFormData.meeting_start_dt = newStartDate;
      updatedFormData.meeting_start_dt_error = false;

      // Check if start date is greater than end date, or if end date is not set
      const currentEndDate = updatedFormData.meeting_end_dt;
      if (
        !currentEndDate ||
        new Date(newStartDate) >= new Date(currentEndDate)
      ) {
        // Update end date to be 2 days after start date at 5:00 PM
        updatedFormData.meeting_end_dt = getDefaultEndDate(newStartDate);
        updatedFormData.meeting_end_dt_error = false;
      }

      setFormData(updatedFormData);
    }
  };

  const handleEndDateChange = (newValue) => {
    const updatedFormData = { ...formData };
    setErrorMsg("");

    if (newValue !== null) {
      let selectedDate = new Date(newValue.$d);
      const year = selectedDate.getFullYear();
      let month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      let day = String(selectedDate.getDate()).padStart(2, "0");
      let hours = String(selectedDate.getHours()).padStart(2, "0");
      let minutes = String(selectedDate.getMinutes()).padStart(2, "0");

      updatedFormData.meeting_end_dt = `${year}-${month}-${day} ${hours}:${minutes}:00`;
      updatedFormData.meeting_end_dt_error = false;
      setFormData(updatedFormData);
    }
  };

  const handleEditorChange = (event, editor) => {
    setSuccessMsg("");
    setErrorMsg("");

    // Update ref instead of state to avoid re-renders during typing
    editorContentRef.current = editor.getData();

    // Only update error state if needed
    if (formData.meeting_content_error) {
      setFormData((prev) => ({
        ...prev,
        meeting_content_error: false,
      }));
    }
  };

  // File upload handlers
  const handleImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setImagePreview(URL.createObjectURL(file));
      setFormData((prev) => ({
        ...prev,
        meeting_image: file,
        meeting_image_name: file.name,
        meeting_image_error: false,
      }));
    }
  };

  const handleDocumentChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setDocPreview(URL.createObjectURL(file));
      setFormData((prev) => ({
        ...prev,
        meeting_documents: file,
        meeting_documents_name: file.name,
        meeting_documents_error: false,
      }));
    }
  };

  const clearUpload = (field, clearPreview) => {
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Form submission handler
  const submitMeeting = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");
    let hasError = false;
    const _required = ["meeting_title", "meeting_start_dt", "meeting_end_dt"];

    const updatedFormData = { ...formData };
    // Iterate _required items for validation
    _required.forEach((field) => {
      if (!updatedFormData[field]) {
        updatedFormData[`${field}_error`] = true;
        hasError = true;
      }
    });

    if (!editorContentRef.current) {
      updatedFormData.meeting_content_error = true;
      hasError = true;
    }

    if (hasError) {
      setFormData(updatedFormData);
      setLoading(false);
      return;
    }

    // Prepare form data
    const formDataToSubmit = new FormData();
    formDataToSubmit.append("meeting_id", updatedFormData.meeting_id || 0);
    formDataToSubmit.append("meeting_title", updatedFormData.meeting_title);
    formDataToSubmit.append("meeting_content", editorContentRef.current);
    formDataToSubmit.append("meeting_video", updatedFormData.meeting_video);
    formDataToSubmit.append(
      "meeting_location",
      updatedFormData.meeting_location || "",
    );
    formDataToSubmit.append(
      "meeting_start_dt",
      updatedFormData.meeting_start_dt,
    );
    formDataToSubmit.append("meeting_end_dt", updatedFormData.meeting_end_dt);

    if (
      updatedFormData.meeting_image &&
      typeof updatedFormData.meeting_image !== "string"
    ) {
      formDataToSubmit.append("meeting_image", updatedFormData.meeting_image);
    }

    if (
      updatedFormData.meeting_documents &&
      typeof updatedFormData.meeting_documents !== "string"
    ) {
      formDataToSubmit.append(
        "meeting_documents",
        updatedFormData.meeting_documents,
      );
    }

    // Submit the form
    addOrUpdateMeeting(formDataToSubmit)
      .then((res) => {
        if (res.data.status === true) {
          const actionType =
            updatedFormData.meeting_id === 0 ? "added" : "updated";
          setSuccessMsg(`Meeting ${actionType} successfully!`);
          resetForm();

          // Switch to the meetings list tab after a delay
          setTimeout(() => {
            setSuccessMsg("");
            setErrorMsg("");
            setActiveTab("2");
          }, 1000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch((error) => {
        log.error("Error submitting meeting:", error);
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Edit meeting handler
  const handleEdit = (meeting) => {
    // Fetch the full meeting details
    getMeeting(meeting.meeting_id)
      .then((res) => {
        if (res.data.status === true) {
          const meetingData = res.data.data;

          // Map API response to component state
          setFormData({
            ...meetingDefault,
            meeting_id: meetingData.meeting_id,
            meeting_title: meetingData.meeting_title,
            meeting_content: meetingData.meeting_content,
            meeting_video: meetingData.meeting_video || "",
            meeting_location: meetingData.meeting_location || "",
            meeting_start_dt: meetingData.meeting_start_dt,
            meeting_end_dt: meetingData.meeting_end_dt,
            meeting_image_name: meetingData.meeting_image || "",
            meeting_documents_name: meetingData.meeting_documents || "",
            // Override default dates with actual data from API
            meeting_start_dt_error: false,
            meeting_end_dt_error: false,
          });

          editorContentRef.current = meetingData.meeting_content || "";

          // Set image previews if available
          if (meetingData.meeting_image) {
            setImagePreview(
              pathJoinUrl(storage[0].imageEndUrl, meetingData.meeting_image),
            );
          }

          if (meetingData.meeting_documents) {
            setDocPreview(
              pathJoinUrl(
                storage[0].imageEndUrl,
                meetingData.meeting_documents,
              ),
            );
          }

          // Switch to edit tab
          setActiveTab("1");
        } else {
          setErrorMsg("Unable to load meeting details");
        }
      })
      .catch((error) => {
        log.error("Error loading meeting details:", error);
        setErrorMsg("Error loading meeting details");
      });
  };

  // Delete meeting handlers
  const handleDelete = (meeting) => {
    setDeleteItem(meeting);
  };

  const handleDeleteSuccess = () => {
    setDeleteItem(null);
    fetchTableData();
  };

  // Reset form handler
  const resetForm = () => {
    setImagePreview("");
    setDocPreview("");
    setSuccessMsg("");
    setErrorMsg("");
    setLoading(false);
    // Create new default values with fresh dates
    const freshDefaults = {
      ...meetingDefault,
      meeting_start_dt: getDefaultStartDate(),
      meeting_end_dt: getDefaultEndDate(),
    };
    setFormData(freshDefaults);
    editorContentRef.current = "";
  };

  // Search and filter handlers
  const handleSearchTypeChange = (type) => {
    setTableData((prev) => ({
      ...prev,
      search: {
        ...prev,
        type,
        text: "",
      },
    }));
  };

  const handleSearchTextChange = (text) => {
    setTableData((prev) => ({
      ...prev,
      search: {
        ...prev.search, // Fix: spread prev.search, not prev
        text,
      },
    }));
  };

  const handleSearch = () => {
    fetchTableData({
      pageNo: 1,
      searchText: tableData.search.text,
      searchType: tableData.search.type,
      sortField: tableData.sort.field,
      sortDirection: tableData.sort.direction,
    });
  };

  const clearFilters = () => {
    setTableData((prev) => ({
      ...prev,
      search: {
        text: "",
        type: "meeting_title",
        startDate: null,
        endDate: null,
      },
    }));

    fetchTableData({
      pageNo: 1,
      searchText: "",
      searchType: "meeting_title",
      sortField: tableData.sort.field,
      sortDirection: tableData.sort.direction,
    });
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    fetchTableData({
      pageNo: page,
      sortField: tableData.sort.field,
      sortDirection: tableData.sort.direction,
    });
  };

  const handlePerPageChange = (perPage) => {
    fetchTableData({
      pageNo: 1,
      perPage,
      sortField: tableData.sort.field,
      sortDirection: tableData.sort.direction,
    });
  };

  // Sort handlers
  const handleSort = (field, direction) => {
    setTableData((prev) => ({
      ...prev,
      sort: { field, direction },
      pagination: { ...prev.pagination, pageNo: 1 },
    }));

    fetchTableData({
      pageNo: 1,
      sortField: field,
      sortDirection: direction,
    });
  };

  // Table columns configuration
  const tableColumns = [
    {
      field: "meeting_title",
      label: "Title",
      width: "30%",
      sortable: true,
    },
    {
      field: "meeting_start_dt",
      label: "Start Date",
      width: "18%",
      sortable: true,
      render: (row) => formatDate(row.meeting_start_dt),
    },
    {
      field: "meeting_end_dt",
      label: "End Date",
      width: "15%",
      render: (row) => formatDate(row.meeting_end_dt),
    },
    {
      field: "meeting_location",
      label: "Location",
      width: "15%",
      render: (row) =>
        row.meeting_location ? (
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <PlaceIcon fontSize="small" sx={{ mr: 0.5 }} />
            {row.meeting_location}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not specified
          </Typography>
        ),
    },
    {
      field: "meeting_image",
      label: "Image",
      width: "10%",
      render: (row) =>
        row.meeting_image ? (
          <Box sx={{ width: 50, height: 40 }}>
            <img
              src={pathJoinUrl(storage[0].imageEndUrl, row.meeting_image)}
              alt={row.meeting_title}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No image
          </Typography>
        ),
    },
    {
      field: "actions",
      label: "Actions",
      width: "15%",
      render: (row) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // Search options for select field
  const searchOptions = [
    { value: "meeting_title", label: "Title" },
    // { value: "meeting_start_dt", label: "Start Date" },
    // { value: "meeting_end_dt", label: "End Date" },
    // { value: "meeting_location", label: "Location" },
  ];

  return (
    <ToolBody>
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <TabList onChange={handleTabChange} aria-label="meetings tabs">
            <Tab
              label={
                formData.meeting_id !== 0 ? "Update Meeting" : "Add Meeting"
              }
              value="1"
            />
            <Tab label="Manage Meetings" value="2" />
          </TabList>
        </Box>

        {/* Add/Edit Meeting Tab */}
        <TabPanel value="1">
          <Stack spacing={3}>
            {/* Title and Location */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LimitedTextField
                  name="meeting_title"
                  label="Title"
                  value={formData.meeting_title}
                  onChange={handleFieldChange}
                  maxLength={formData.meeting_title_maxlength}
                  error={formData.meeting_title_error}
                  errorText="Please enter a title"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <LimitedTextField
                  name="meeting_location"
                  label="Location"
                  value={formData.meeting_location}
                  onChange={handleFieldChange}
                  maxLength={formData.meeting_location_maxlength}
                  error={formData.meeting_location_error}
                  errorText="Please enter a location"
                />
              </Grid>
            </Grid>

            {/* Meeting Dates */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDateTimePicker
                    label="Start Date & Time"
                    value={
                      formData.meeting_start_dt
                        ? dayjs(formData.meeting_start_dt)
                        : null
                    }
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        error: formData.meeting_start_dt_error,
                        helperText: formData.meeting_start_dt_error
                          ? "Please select a start date and time"
                          : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDateTimePicker
                    label="End Date & Time"
                    value={
                      formData.meeting_end_dt
                        ? dayjs(formData.meeting_end_dt)
                        : null
                    }
                    onChange={handleEndDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        error: formData.meeting_end_dt_error,
                        helperText: formData.meeting_end_dt_error
                          ? "Please select an end date and time"
                          : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            {/* Meeting Content */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Meeting Content
              </Typography>
              <RichTextEditor
                data={
                  formData.meeting_id
                    ? formData.meeting_content
                    : editorContentRef.current
                }
                onChange={handleEditorChange}
              />
              {formData.meeting_content_error && (
                <FormHelperText error>
                  Please enter meeting content
                </FormHelperText>
              )}
            </Box>

            {/* Files Upload */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FileUploadArea
                  title="Upload Document (PDF)"
                  description={
                    <>
                      Best file type: PDF
                      <br />
                      Max file size: 10MB
                    </>
                  }
                  previewUrl={docPreview}
                  fileName={formData.meeting_documents_name}
                  onChange={handleDocumentChange}
                  deleteRequest={{
                    resource: "meeting",
                    id: formData.meeting_id,
                    field: "meeting_documents",
                  }}
                  onRemove={() =>
                    clearUpload("meeting_documents", setDocPreview)
                  }
                  accept="application/pdf"
                  error={formData.meeting_documents_error}
                  errorText="Please upload a PDF document"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FileUploadArea
                  title="Upload Meeting Image"
                  description={
                    <>
                      Best image Resolution: 800px X 600px
                      <br />
                      Supported file types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={imagePreview}
                  fileName={formData.meeting_image_name}
                  onChange={handleImageChange}
                  deleteRequest={{
                    resource: "meeting",
                    id: formData.meeting_id,
                    field: "meeting_image",
                  }}
                  onRemove={() => clearUpload("meeting_image", setImagePreview)}
                  accept=".png,.jpg,.jpeg"
                  error={formData.meeting_image_error}
                  errorText="Please upload an image"
                />
              </Grid>
            </Grid>

            {/* Meeting Archive Video */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Meeting Archive Video (Post-meeting video URL/HTML)
                </Typography>
                <LimitedTextField
                  name="meeting_video"
                  label="Meeting Archive Video"
                  value={formData.meeting_video}
                  onChange={handleFieldChange}
                  maxLength={formData.meeting_video_maxlength}
                  error={formData.meeting_video_error}
                  errorText="Please enter a video URL or HTML"
                  multiline
                  rows={4}
                  placeholder="Paste video URL or HTML embed code here"
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <FormActionBar
              onSubmit={submitMeeting}
              onReset={resetForm}
              isSubmitting={loading}
              isEdit={formData.meeting_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel={formData.meeting_id !== 0 ? "Update" : "Save"}
              resetLabel={formData.meeting_id !== 0 ? "Create New" : "Reset"}
            />
          </Stack>
        </TabPanel>

        {/* Manage Meetings Tab */}
        <TabPanel value="2">
          {/* Search Controls */}
          <SearchControls
            searchText={tableData.search.text}
            searchType={tableData.search.type}
            searchOptions={searchOptions}
            onSearchChange={handleSearchTextChange}
            onTypeChange={handleSearchTypeChange}
            onSubmit={handleSearch}
            onClear={clearFilters}
            perPage={tableData.pagination.perPage}
            totalCount={tableData.pagination.totalCount}
            onPerPageChange={handlePerPageChange}
          />

          {/* Data Table */}
          <DataTable
            columns={tableColumns}
            data={tableData.data}
            loading={tableData.loading}
            sortConfig={tableData.sort}
            onSort={handleSort}
            keyField="meeting_id"
            noDataMessage="No meetings found"
          />

          {/* Pagination */}
          {tableData.pagination.totalPage > 1 && (
            <TablePagination
              page={tableData.pagination.pageNo}
              totalPages={tableData.pagination.totalPage}
              loading={tableData.loading}
              onChange={handlePageChange}
              entriesPerPage={tableData.pagination.perPage}
              onEntriesChange={handlePerPageChange}
            />
          )}
        </TabPanel>
      </TabContext>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteItem !== null}
        title="Delete Meeting"
        message="Are you sure you want to delete this meeting?"
        handleClose={() => setDeleteItem(null)}
        handleSuccess={handleDeleteSuccess}
        deleteAction={deleteMeeting}
        data={deleteItem ? deleteItem.meeting_id : null}
      />
    </ToolBody>
  );
};

export default Meetings;
